/**
 * Disposition des widgets sur une grille de 12 colonnes (x, y, w, h en unités de grille).
 * Fonctions pures : chaque opération renvoie un nouveau layout compacté vers le haut.
 */
export const COLS = 12

const clone = layout => layout.map(item => ({ ...item }))
const bottom = item => item.y + item.h
const collides = (a, b) => a.i !== b.i && a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y

const limits = (item, defs) => {
  const def = (defs && defs[item.module]) || {}
  return { minW: def.minW || 2, minH: def.minH || 2, maxH: def.maxH || 40 }
}

// Compactage vertical : chaque widget remonte au plus haut sans chevaucher ceux déjà placés (ordre y puis x)
export function compact(layout) {
  const sorted = clone(layout).sort((a, b) => a.y - b.y || a.x - b.x)
  const placed = []
  sorted.forEach(item => {
    item.x = Math.max(0, Math.min(item.x, COLS - item.w))
    item.y = 0
    while (placed.some(p => collides(item, p))) item.y++
    placed.push(item)
  })
  return placed
}

/**
 * Format repris d'un widget de référence : même type de widget, ou format qui respecte les limites du nouveau
 * @returns {{w, h}|null}
 */
export function sizeLike(item, ref, defs) {
  if (!ref) return null
  const lim = limits(item, defs)
  const fits = ref.w >= lim.minW && ref.h >= lim.minH && ref.h <= lim.maxH
  if (ref.module !== item.module && !fits) return null
  return { w: Math.max(lim.minW, Math.min(COLS, ref.w)), h: Math.max(lim.minH, Math.min(lim.maxH, ref.h)) }
}

// Widget juste au-dessus (colonnes communes, le plus proche)
function itemAbove(layout, item) {
  const overlap = it => Math.min(it.x + it.w, item.x + item.w) - Math.max(it.x, item.x)
  return layout
    .filter(it => it.i !== item.i && bottom(it) <= item.y && overlap(it) > 0)
    .sort((a, b) => bottom(b) - bottom(a) || overlap(b) - overlap(a))[0] || null
}

// Widget sous un point de la grille (hors widget déplacé)
function itemAt(layout, x, y, excludeId) {
  return layout.find(item => item.i !== excludeId && x >= item.x && x < item.x + item.w && y >= item.y && y < bottom(item))
}

// Largeurs égales sur une ligne (12 → 6/6 → 4/4/4…), le reste distribué aux premiers
function equalWidths(count) {
  const base = Math.floor(COLS / count)
  const rest = COLS - base * count
  return Array.from({ length: count }, (_, idx) => base + (idx < rest ? 1 : 0))
}

/**
 * Dépôt d'un widget : posé sur un autre widget, il rejoint sa ligne et les largeurs sont rééquilibrées
 * (pleine largeur → 50/50, deux widgets → 4/4/4) dans la limite de lisibilité de chacun ; sinon il passe dessous.
 * @param {Array} snapshot - layout au début du déplacement
 * @param {Object} [pointer] - case de la grille sous le curseur au dépôt ({col, row}) : un widget pleine largeur ne peut pas bouger en x
 * @param {Object} [opts] - isNew : widget qu'on ajoute, il reprend le format du widget au-dessus quand c'est possible
 */
export function placeMoved(snapshot, id, x, y, defs, pointer, opts = {}) {
  const layout = clone(snapshot)
  const moved = layout.find(item => item.i === id)
  if (!moved) return compact(layout)
  const others = layout.filter(item => item.i !== id)
  const centerX = pointer ? pointer.col + 0.5 : x + moved.w / 2
  const targetRow = pointer ? pointer.row : y
  const target = itemAt(others, Math.max(0, Math.min(COLS - 1, Math.floor(centerX))), targetRow, id)

  if (!target) {
    moved.x = Math.max(0, Math.min(x, COLS - moved.w))
    moved.y = Math.max(0, y)
    const size = opts.isNew ? sizeLike(moved, itemAbove(others, moved), defs) : null
    if (size) {
      moved.w = size.w
      moved.h = size.h
      moved.x = Math.max(0, Math.min(x, COLS - moved.w))
    }
    return settle(layout, moved)
  }

  const row = others.filter(item => item.y === target.y).sort((a, b) => a.x - b.x)
  const insertAt = row.findIndex(item => centerX < item.x + item.w / 2)
  const ordered = [...row]
  ordered.splice(insertAt === -1 ? row.length : insertAt, 0, moved)
  const widths = equalWidths(ordered.length)

  if (ordered.every((item, idx) => widths[idx] >= limits(item, defs).minW)) {
    let cursor = 0
    ordered.forEach((item, idx) => {
      item.x = cursor
      item.w = widths[idx]
      item.y = target.y
      cursor += widths[idx]
    })
    // Même hauteur que la ligne rejointe quand ses limites le permettent (présentation alignée)
    const lim = limits(moved, defs)
    if (target.h >= lim.minH && target.h <= lim.maxH) moved.h = target.h
    return settle(layout, moved)
  }

  // Pas assez de place pour rester lisible : sous la ligne visée
  moved.x = Math.max(0, Math.min(x, COLS - moved.w))
  moved.y = Math.max(...row.map(bottom))
  return settle(layout, moved)
}

/**
 * Redimensionnement : les voisins de droite de la même ligne cèdent ou récupèrent la place, sans passer sous leur minimum
 * @param {Array} snapshot - layout au début du redimensionnement
 */
export function applyResize(snapshot, id, w, h, defs) {
  const layout = clone(snapshot)
  const item = layout.find(it => it.i === id)
  if (!item) return compact(layout)
  const lim = limits(item, defs)
  const oldRight = item.x + item.w
  item.h = Math.max(lim.minH, Math.min(lim.maxH, h))
  let width = Math.max(lim.minW, Math.min(w, COLS - item.x))

  const right = layout.filter(it => it.i !== id && it.y === item.y && it.x >= oldRight).sort((a, b) => a.x - b.x)
  const rowWasFull = right.length > 0 && right[right.length - 1].x + right[right.length - 1].w === COLS
  let excess = item.x + width + right.reduce((s, it) => s + it.w, 0) - COLS

  if (excess > 0) {
    right.forEach(it => {
      if (excess <= 0) return
      const give = Math.min(excess, it.w - limits(it, defs).minW)
      it.w -= give
      excess -= give
    })
    if (excess > 0) width -= excess
  } else if (excess < 0 && rowWasFull && right.length) {
    // Rétrécissement d'une ligne pleine : le voisin immédiat récupère la place libérée
    right[0].w -= excess
  }

  item.w = width
  let cursor = item.x + item.w
  right.forEach(it => {
    it.x = cursor
    cursor += it.w
  })
  return settle(layout, item)
}

const mirror = layout => layout.map(it => ({ ...it, x: COLS - it.x - it.w }))

/**
 * Redimensionnement par un bord ou un coin : rect {x, w, h} visé (x change seulement par le bord gauche).
 * Les voisins de la ligne, côté du bord tiré, cèdent ou récupèrent la largeur ; la hauteur se règle par le haut ou le bas.
 */
export function resizeTo(snapshot, id, rect, defs) {
  const item = snapshot.find(it => it.i === id)
  if (!item) return compact(clone(snapshot))
  if (rect.x === item.x) return applyResize(snapshot, id, rect.w, rect.h, defs)
  return mirror(applyResize(mirror(snapshot), id, rect.w, rect.h, defs))
}

/**
 * Ajout : même format que le dernier widget quand c'est possible, à sa droite s'il reste la place sur sa ligne, sinon dessous
 */
export function addItem(layout, item, defs) {
  const last = clone(layout).sort((a, b) => bottom(b) - bottom(a) || b.x - a.x)[0]
  const added = { ...item, x: 0, y: layout.length ? Math.max(...layout.map(bottom)) : 0 }
  const size = defs ? sizeLike(added, last, defs) : null
  if (size) {
    added.w = size.w
    added.h = size.h
    const rowEnd = Math.max(...layout.filter(it => it.y === last.y).map(it => it.x + it.w))
    if (COLS - rowEnd >= added.w) {
      added.x = rowEnd
      added.y = last.y
    }
  }
  return compact([...clone(layout), added])
}

// Retrait : le voisin de gauche (sinon de droite) de la même ligne récupère la largeur libérée
export function removeItem(layout, id) {
  const removed = layout.find(item => item.i === id)
  const rest = clone(layout).filter(item => item.i !== id)
  if (removed) {
    const row = rest.filter(item => item.y === removed.y).sort((a, b) => a.x - b.x)
    const left = row.filter(item => item.x + item.w === removed.x).pop()
    const right = row.find(item => item.x === removed.x + removed.w)
    if (left) left.w += removed.w
    else if (right) {
      right.x = removed.x
      right.w += removed.w
    }
  }
  return compact(rest)
}

// Les widgets chevauchés par celui qu'on vient de placer descendent, puis tout remonte
function settle(layout, anchor) {
  const sorted = layout.filter(item => item !== anchor).sort((a, b) => a.y - b.y || a.x - b.x)
  const placed = [anchor]
  sorted.forEach(item => {
    while (placed.some(p => collides(item, p))) item.y++
    placed.push(item)
  })
  return compact(placed)
}

// Lecture sur petit écran : une colonne, dans l'ordre visuel
export function readingOrder(layout) {
  return clone(layout).sort((a, b) => a.y - b.y || a.x - b.x)
}
